import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { usePartnerDetails } from "../../hooks/usePartnerDetails";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { Text } from "../../../../components/common/typography";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { IconUsers } from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { Button } from "../../../../components/common/button";
import { PartnerHeader } from "../components/partner-header";
import { PartnerOverviewTab } from "../components/partner-overview-tab";
import { PartnerOrdersTab } from "../components/partner-orders-tab";
import { PartnerProductsTab } from "../components/partner-products-tab";
import { PartnerPaymentsTab } from "../components/partner-payments-tab";
import { PartnerContactsAddressesTab } from "../components/partner-contacts-addresses-tab";

type TabValue = "overview" | "orders" | "products" | "payments" | "contacts";

export function PartnerDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { backLabel?: string; backPath?: string } | null;
  const [searchParams, setSearchParams] = useSearchParams();
  const partnerId = Number(id ?? 0);
  const { data: partner, error, isLoading } = usePartnerDetails(partnerId);

  const isSeller = (partner?.supplierRank ?? 0) > 0;

  const tabs: { value: TabValue; label: string }[] = [
    { value: "overview", label: "Overview" },
    { value: "orders", label: "Orders" },
    ...(isSeller ? [{ value: "products" as TabValue, label: "Products" }] : []),
    { value: "payments", label: "Payments" },
    { value: "contacts", label: "Contacts & Addresses" },
  ];

  const rawTab = searchParams.get("tab") as TabValue | null;
  const activeTab: TabValue = tabs.some((t) => t.value === rawTab) ? (rawTab as TabValue) : "overview";

  function handleTabChange(tab: TabValue) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
        <ShimmerLoader message="Loading partner…">
          <div className="flex flex-col gap-6">
            <Shimmer className="h-20 rounded-2xl" />
            <Shimmer className="h-10 w-96 rounded-2xl" />
            <div className="flex gap-6">
              <Shimmer className="h-48 flex-1 rounded-xl" />
              <Shimmer className="h-48 flex-1 rounded-xl" />
            </div>
            <Shimmer className="h-36 rounded-xl" />
            <Shimmer className="h-64 rounded-xl" />
          </div>
        </ShimmerLoader>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
        <EmptyState
          icon={IconUsers}
          title="Partner not found"
          description="This partner may have been removed or you don't have access."
          primaryAction={{ label: "Back to Partners", onClick: () => navigate(AppRoutes.PARTNERS) }}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <div className="flex items-center gap-2">
        <Button variant="ghostAccent" onClick={() => navigate(navState?.backPath ?? AppRoutes.PARTNERS)} className="!px-0">
          {navState?.backLabel ?? "← Partners"}
        </Button>
        <Text variant="body" tone="muted">/</Text>
        <Text variant="body" tone="muted">{partner.name || `Partner #${partner.id}`}</Text>
      </div>

      <PartnerHeader partner={partner} />

      <SegmentedControl
        options={tabs}
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Partner details tabs"
      />

      {activeTab === "overview" && <PartnerOverviewTab partner={partner} />}
      {activeTab === "orders" && <PartnerOrdersTab partnerId={partner.id} isSeller={isSeller} />}
      {activeTab === "products" && isSeller && <PartnerProductsTab partnerId={partner.id} />}
      {activeTab === "payments" && <PartnerPaymentsTab partnerId={partner.id} />}
      {activeTab === "contacts" && <PartnerContactsAddressesTab partnerId={partner.id} />}
    </div>
  );
}
